import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import {
  Loader2,
  Plus,
  ExternalLink,
  FileQuestion,
  ChevronDown,
  ChevronRight,
  Pencil,
  Copy,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { onboardingAPI } from '../../../lib/api';
import type { OnboardingDataset, DatasetSection, DatasetQuestion, OnboardingConfig } from '../../../lib/api';
import { toast } from 'sonner';

interface DatasetWithDetails extends OnboardingDataset {
  sections?: (DatasetSection & { questions?: DatasetQuestion[] })[];
}

interface OnboardingTabProps {
  onboardingEnabled?: boolean;
  onToggleOnboarding?: (enabled: boolean) => void;
}

export function OnboardingTab({ onboardingEnabled = true, onToggleOnboarding }: OnboardingTabProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [datasets, setDatasets] = useState<DatasetWithDetails[]>([]);
  const [expandedDataset, setExpandedDataset] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [config, setConfig] = useState<OnboardingConfig | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);

  const loadDatasets = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await onboardingAPI.getDatasets({ page_size: 100, dataset_type: 'risk_assessment' });
      const results = resp.data.results || [];
      setDatasets(results);
    } catch (error) {
      console.error('Error loading risk datasets:', error);
      toast.error('Failed to load risk assessment datasets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDatasets();
    onboardingAPI.getOnboardingConfig()
      .then(res => setConfig(res.data))
      .catch(() => {/* tolerate missing config endpoint */});
  }, [loadDatasets]);

  const updateConfig = async (patch: Partial<OnboardingConfig>) => {
    if (!config) return;
    setSavingConfig(true);
    const prev = config;
    setConfig({ ...config, ...patch });
    try {
      const res = await onboardingAPI.updateOnboardingConfig(patch);
      setConfig(res.data);
      toast.success('Onboarding settings saved');
    } catch {
      setConfig(prev);
      toast.error('Failed to update onboarding settings');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleToggleActive = async (dataset: DatasetWithDetails) => {
    try {
      setTogglingId(dataset.id);
      await onboardingAPI.updateDataset(dataset.id, { is_active: !dataset.is_active });
      setDatasets(prev => prev.map(d => d.id === dataset.id ? { ...d, is_active: !d.is_active } : d));
      toast.success(`${dataset.name} ${dataset.is_active ? 'deactivated' : 'activated'}`);
    } catch (error) {
      console.error('Error toggling dataset:', error);
      toast.error('Failed to update dataset');
    } finally {
      setTogglingId(null);
    }
  };

  const handleClone = async (id: number) => {
    try {
      const resp = await onboardingAPI.cloneDataset(id);
      setDatasets(prev => [...prev, resp.data]);
      toast.success('Dataset cloned successfully');
    } catch (error) {
      console.error('Error cloning dataset:', error);
      toast.error('Failed to clone dataset');
    }
  };

  const handleDelete = async (id: number, name: string) => {
    try {
      await onboardingAPI.deleteDataset(id);
      setDatasets(prev => prev.filter(d => d.id !== id));
      toast.success(`Deleted "${name}"`);
    } catch {
      toast.error('Cannot delete — may be in use by existing suppliers');
    }
  };

  const handleExpandDataset = async (id: number) => {
    if (expandedDataset === id) {
      setExpandedDataset(null);
      return;
    }
    setExpandedDataset(id);
    // Load full dataset with sections/questions if not already loaded
    const existing = datasets.find(d => d.id === id);
    if (existing && existing.sections && existing.sections.length > 0) return;
    try {
      const resp = await onboardingAPI.getDataset(id);
      setDatasets(prev => prev.map(d => d.id === id ? { ...d, ...resp.data } : d));
    } catch (error) {
      console.error('Error loading dataset details:', error);
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'risk_assessment': return 'bg-red-100 text-red-800';
      case 'onboarding': return 'bg-blue-100 text-blue-800';
      case 'compliance': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Master Toggle */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-base">Supplier Onboarding Workflow</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {onboardingEnabled
                  ? 'New suppliers go through the full workflow: initial approval (customer-side) → invitation email → supplier fills questionnaires → submitted for review → approved or declined.'
                  : 'New suppliers are added directly as approved without an approval workflow.'}
              </p>
            </div>
            <Switch
              checked={onboardingEnabled}
              onCheckedChange={(checked) => onToggleOnboarding?.(checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Self-Approval Toggle */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-base">Allow Self-Approval</h3>
              <p className="text-sm text-muted-foreground mt-1">
                When on, a user can approve questionnaires they invited themselves. Leave off if a
                second reviewer is required.
              </p>
            </div>
            <Switch
              checked={!!config?.allow_self_approval}
              disabled={!config || savingConfig}
              onCheckedChange={(checked) => updateConfig({ allow_self_approval: checked })}
            />
          </div>
        </CardContent>
      </Card>

      <p className="text-sm text-muted-foreground">
        Configure supplier onboarding risk assessment questions. These questions appear in Step 5 of the Add Supplier wizard.
        Each question can have configurable scoring and weightage.
      </p>

      {/* Risk Assessment Datasets */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg">Risk Assessment Datasets</CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/onboarding/datasets')}
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              All Datasets
            </Button>
            <Button
              size="sm"
              onClick={() => navigate('/onboarding/datasets/new')}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              New Dataset
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {datasets.length === 0 ? (
            <div className="text-center py-8 bg-muted/30 rounded-lg border-2 border-dashed">
              <FileQuestion className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground mb-3">No risk assessment datasets configured yet.</p>
              <Button size="sm" onClick={() => navigate('/onboarding/datasets/new')}>
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Create Risk Assessment
              </Button>
            </div>
          ) : (
            datasets.map(dataset => {
              const isExpanded = expandedDataset === dataset.id;
              const sections = dataset.sections || [];
              const allQuestions = sections.flatMap(s => s.questions || []);

              return (
                <div key={dataset.id} className="border rounded-lg overflow-hidden">
                  {/* Dataset header */}
                  <div
                    className="flex items-center gap-3 p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => handleExpandDataset(dataset.id)}
                  >
                    <div className="shrink-0">
                      {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-sm">{dataset.name}</span>
                        <Badge className={`text-[10px] px-1.5 py-0 ${getTypeColor(dataset.dataset_type)}`}>
                          {dataset.dataset_type.replace(/_/g, ' ')}
                        </Badge>
                        {dataset.is_published && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-green-300 text-green-700">Published</Badge>
                        )}
                        {!dataset.is_active && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-yellow-300 text-yellow-700">
                            <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
                            Inactive
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {dataset.company_name && <span>Customer: {dataset.company_name}</span>}
                        <span>{dataset.questions_count || allQuestions.length} questions</span>
                        {dataset.pass_score > 0 && <span>Pass score: {dataset.pass_score}%</span>}
                        <span>v{dataset.version}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
                      <span className="text-xs text-muted-foreground">Active</span>
                      <Switch
                        checked={dataset.is_active}
                        disabled={togglingId === dataset.id}
                        onCheckedChange={() => handleToggleActive(dataset)}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => navigate(`/onboarding/datasets/${dataset.id}`)}
                        title="Edit in Dataset Builder"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleClone(dataset.id)}
                        title="Clone dataset"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(dataset.id, dataset.name)}
                        title="Delete dataset"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Expanded: show questions */}
                  {isExpanded && (
                    <div className="border-t bg-muted/20 p-4 space-y-3">
                      {sections.length === 0 && allQuestions.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-2">
                          No questions configured. Click the edit button to add questions in the Dataset Builder.
                        </p>
                      ) : (
                        sections.map(section => (
                          <div key={section.id} className="space-y-2">
                            {sections.length > 1 && (
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                {section.title}
                                {section.weight ? ` (Weight: ${section.weight})` : ''}
                              </p>
                            )}
                            <div className="grid gap-2">
                              {(section.questions || []).map((q, qIdx) => (
                                <div key={q.id} className="flex items-start gap-3 p-3 bg-white rounded-md border">
                                  <span className="text-xs font-bold text-muted-foreground mt-0.5 shrink-0">{qIdx + 1}.</span>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm">{q.question_text}</p>
                                    {q.help_text && <p className="text-xs text-muted-foreground mt-0.5">{q.help_text}</p>}
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                      {q.question_type.replace(/_/g, ' ')}
                                    </Badge>
                                    {q.is_scored && q.max_score && (
                                      <Badge className="text-[10px] px-1.5 py-0 bg-blue-100 text-blue-800">
                                        Max: {q.max_score}
                                      </Badge>
                                    )}
                                    {q.is_required && (
                                      <Badge className="text-[10px] px-1.5 py-0 bg-red-100 text-red-800">Required</Badge>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))
                      )}
                      <div className="pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/onboarding/datasets/${dataset.id}`)}
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1.5" />
                          Edit Questions & Weightage
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Info card */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <FileQuestion className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="text-sm space-y-1">
              <p className="font-medium text-blue-900">How risk assessments work</p>
              <ul className="text-blue-800/80 space-y-0.5 list-disc list-inside text-xs">
                <li>Each customer company can have one active risk assessment dataset</li>
                <li>Questions appear in Step 5 (Approval Stage) of the supplier onboarding wizard</li>
                <li>Scoring and weightage are configured per question in the Dataset Builder</li>
                <li>Risk level is calculated from scored answers: Low (&lt;33%), Medium (33-66%), High (&gt;66%)</li>
                <li>Use the Dataset Builder for full control over sections, question types, and scoring</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
